import React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Space } from 'antd';

/**
 * getColumnSearchProps - Cria props para filtro de busca em colunas de tabela
 * Compatível com Ant Design v5 Table
 *
 * @param {string} dataIndex - Nome da coluna para filtrar
 * @param {React.RefObject} searchInput - Ref para o input de busca
 * @returns {Object} Props para usar em column.filterDropdown
 * @example
 * const columns = [
 *   {
 *     title: 'Nome',
 *     dataIndex: 'nome',
 *     ...getColumnSearchProps('nome')
 *   }
 * ];
 */
export const getColumnSearchProps = (dataIndex, searchInput = null) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
            <Input
                ref={searchInput}
                placeholder={`Buscar ${dataIndex}`}
                value={selectedKeys[0]}
                onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onPressEnter={() => confirm()}
                style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
                <Button
                    type="primary"
                    onClick={() => confirm()}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90 }}
                >
                    Buscar
                </Button>
                <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                    Limpar
                </Button>
            </Space>
        </div>
    ),
    filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
        record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
});

/**
 * mask - Função universal de formatação/máscara de dados
 * Compatível com código migrado do integrar-skin
 *
 * @param {string|number} data - Valor a ser formatado
 * @param {string|null} type - Tipo de formatação: 'currency', 'date', 'cep', 'cpf', 'cnpj', null
 * @returns {string} Valor formatado
 *
 * @example
 * mask(12345678901) // Auto-detect CPF: "123.456.789-01"
 * mask(2500.50, "currency") // "R$ 2.500,50"
 * mask("2024-01-15", "date") // "15/01/2024"
 * mask("13467692", "cep") // "13.467-692"
 */
export const mask = (data, type = null) => {
    // Validação inicial
    if (data === null || data === undefined || data === '') {
        return '';
    }

    try {
        switch (type) {
            case 'currency':
                // Formatação monetária BRL
                const number = parseFloat(data);
                if (isNaN(number)) return 'R$ 0,00';
                return number.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                });

            case 'date':
                // Formatação de data: YYYY-MM-DD -> DD/MM/YYYY
                const dateStr = String(data);
                if (dateStr.length >= 10) {
                    // Formato ISO: 2024-01-15 ou 2024-01-15T...
                    const parts = dateStr.substring(0, 10).split('-');
                    if (parts.length === 3) {
                        return `${parts[2]}/${parts[1]}/${parts[0]}`;
                    }
                }
                return data;

            case 'cep':
                // Formatação CEP: 12345678 -> 12.345-678
                const cep = String(data).replace(/\D/g, '');
                if (cep.length === 8) {
                    return cep.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2-$3');
                }
                return data;

            case 'cpf':
                // Formatação CPF: 12345678901 -> 123.456.789-01
                const cpf = String(data).replace(/\D/g, '');
                if (cpf.length === 11) {
                    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                }
                return data;

            case 'cnpj':
                // Formatação CNPJ: 12345678000195 -> 12.345.678/0001-95
                const cnpj = String(data).replace(/\D/g, '');
                if (cnpj.length === 14) {
                    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
                }
                return data;

            case null:
            case undefined:
                // Auto-detecção CPF/CNPJ baseado no tamanho
                const doc = String(data).replace(/\D/g, '');
                if (doc.length === 11) {
                    // CPF
                    return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
                } else if (doc.length === 14) {
                    // CNPJ
                    return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
                }
                return data;

            default:
                return data;
        }
    } catch (e) {
        // Em caso de erro, retorna o valor original
        return data;
    }
};
